import React, { useCallback, useMemo, useRef, useState } from 'react'
import {
  FolderOpenIcon,
  ArrowPathIcon,
  CalendarDaysIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline'
import { Button, Input, Text } from '../../components/ui'
import { useAuthContext } from '../../contexts/AuthContext'
import { useUserLogs } from '../../hooks/useUserLogs'
import {
  financeiroExtratosContaService,
  pelFileKey,
  pelFolderKey,
} from '../../services/financeiroExtratosConta'
import type {
  FinanceiroExtratoContaTree,
  FinanceiroPelFileProgress,
} from '../../types/financeiroExtratosConta'
import {
  daysInMonthScope,
  distinctAccountsInMonthDayScope,
  flattenScope,
  isShowDirectoryPickerAvailable,
  pickExtratosContaYearFolder,
  sendPelExtratoPdf,
  treeFromWebkitFiles,
} from '../../utils/financeiroExtratosConta'

const PAGE_SIZE = 20
const CONFIRM_OK_ATTEMPTS = 3
const PEL_UNCONFIRMED_STORAGE_KEY = 'financeiro-pel-webhook-accepted'

type UnconfirmedPelFile = {
  key: string
  fileId: string
}

function readUnconfirmedPelFiles(): UnconfirmedPelFile[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(PEL_UNCONFIRMED_STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return []
    return parsed.filter(
      (item): item is UnconfirmedPelFile =>
        !!item &&
        typeof item === 'object' &&
        typeof (item as UnconfirmedPelFile).key === 'string' &&
        typeof (item as UnconfirmedPelFile).fileId === 'string'
    )
  } catch {
    return []
  }
}

function writeUnconfirmedPelFiles(items: UnconfirmedPelFile[]) {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(PEL_UNCONFIRMED_STORAGE_KEY, JSON.stringify(items))
}

function rememberUnconfirmedPelFile(item: UnconfirmedPelFile) {
  const next = readUnconfirmedPelFiles().filter((current) => current.key !== item.key)
  next.push(item)
  writeUnconfirmedPelFiles(next)
}

function forgetUnconfirmedPelFile(key: string) {
  writeUnconfirmedPelFiles(readUnconfirmedPelFiles().filter((item) => item.key !== key))
}

async function confirmPelFileOk(
  fileId: string,
  userId: string | null
): Promise<void> {
  let lastError: unknown
  for (let attempt = 0; attempt < CONFIRM_OK_ATTEMPTS; attempt += 1) {
    try {
      await financeiroExtratosContaService.updateFileStatus({
        fileId,
        status: 'ok',
        userId,
      })
      return
    } catch (err) {
      lastError = err
      if (attempt < CONFIRM_OK_ATTEMPTS - 1) {
        await new Promise((resolve) => setTimeout(resolve, 400 * (attempt + 1)))
      }
    }
  }
  throw lastError instanceof Error
    ? lastError
    : new Error('Não foi possível confirmar o arquivo no banco.')
}

const ExtratosContaTab: React.FC = () => {
  const folderInputRef = useRef<HTMLInputElement>(null)
  const { user } = useAuthContext()
  const { logAction } = useUserLogs()

  const [tree, setTree] = useState<FinanceiroExtratoContaTree | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isListing, setIsListing] = useState(false)

  const [selectedMonths, setSelectedMonths] = useState<string[]>([])
  const [selectedDays, setSelectedDays] = useState<string[]>([])
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([])
  const [accountSearch, setAccountSearch] = useState('')
  const [currentPage, setCurrentPage] = useState(1)

  const [processedFileKeys, setProcessedFileKeys] = useState<Set<string>>(
    () => new Set(readUnconfirmedPelFiles().map((item) => item.key))
  )
  const [isProcessing, setIsProcessing] = useState(false)
  const [fileProgress, setFileProgress] = useState<FinanceiroPelFileProgress[]>([])
  const [currentFileLabel, setCurrentFileLabel] = useState<string | null>(null)

  const refreshProcessedFiles = useCallback(async () => {
    try {
      const pending = readUnconfirmedPelFiles()
      const stillPending: UnconfirmedPelFile[] = []
      const healedKeys = new Set<string>()
      for (const item of pending) {
        try {
          await financeiroExtratosContaService.updateFileStatus({
            fileId: item.fileId,
            status: 'ok',
            userId: user?.id ?? null,
          })
          healedKeys.add(item.key)
        } catch (err) {
          console.error(err)
          stillPending.push(item)
        }
      }
      writeUnconfirmedPelFiles(stillPending)

      await financeiroExtratosContaService.cleanupIncompleteFolders(user?.id ?? null)
      const list = await financeiroExtratosContaService.listArquivosProcessadosOk()
      const keys = new Set(
        list.map((f) => pelFileKey(f.year, f.month, f.day, f.account, f.file_name))
      )
      for (const item of stillPending) keys.add(item.key)
      setProcessedFileKeys(keys)
      if (healedKeys.size > 0) {
        setFileProgress((prev) =>
          prev.map((p) => {
            const key = pelFileKey(p.year, p.month, p.day, p.account, p.fileName)
            if (!healedKeys.has(key)) return p
            return { ...p, status: 'ok', error: undefined }
          })
        )
      }
    } catch (err) {
      console.error(err)
    }
  }, [user?.id])

  React.useEffect(() => {
    void refreshProcessedFiles()
  }, [refreshProcessedFiles])

  const applyTree = useCallback((next: FinanceiroExtratoContaTree) => {
    if (next.months.length === 0) {
      setTree(null)
      setSelectedMonths([])
      setSelectedDays([])
      setSelectedAccounts([])
      setFileProgress([])
      setCurrentFileLabel(null)
      setError(
        'Nenhuma pasta de mês válida encontrada. Selecione a pasta do ano (ex.: "- 2026") com pastas "01 - JANEIRO" ou "1 - JANEIRO", etc.'
      )
      return
    }
    setError(null)
    setTree(next)
    setSelectedMonths([])
    setSelectedDays([])
    setSelectedAccounts([])
    setAccountSearch('')
    setCurrentPage(1)
    setFileProgress([])
    setCurrentFileLabel(null)
  }, [])

  const handleSelectRootClick = async () => {
    if (isListing) return
    setError(null)

    if (isShowDirectoryPickerAvailable()) {
      try {
        setIsListing(true)
        const next = await pickExtratosContaYearFolder()
        setIsListing(false)
        if (!next) return
        applyTree(next)
      } catch (err) {
        setIsListing(false)
        setError(err instanceof Error ? err.message : 'Não foi possível abrir a pasta do ano.')
      }
      return
    }

    folderInputRef.current?.click()
  }

  const handleFolderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileArray = Array.from(e.target.files ?? [])
    e.target.value = ''
    try {
      const next = treeFromWebkitFiles(fileArray)
      if (!next) {
        setError('Nenhuma estrutura mês/dia/PDF encontrada na pasta selecionada.')
        return
      }
      applyTree(next)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível ler a pasta selecionada.')
    }
  }

  const cancelSession = () => {
    if (isProcessing) return
    setTree(null)
    setSelectedMonths([])
    setSelectedDays([])
    setSelectedAccounts([])
    setAccountSearch('')
    setCurrentPage(1)
    setError(null)
    setFileProgress([])
    setCurrentFileLabel(null)
  }

  const scopeRowKey = useCallback(
    (row: { month: string; day: string; account: string; fileName: string }) => {
      if (!tree) return ''
      return pelFileKey(tree.year, row.month, row.day, row.account, row.fileName)
    },
    [tree]
  )

  const availableDays = useMemo(
    () => (tree ? daysInMonthScope(tree, selectedMonths) : []),
    [tree, selectedMonths]
  )

  const availableAccounts = useMemo(
    () => (tree ? distinctAccountsInMonthDayScope(tree, selectedMonths, selectedDays) : []),
    [tree, selectedMonths, selectedDays]
  )

  const filteredAccounts = useMemo(() => {
    const q = accountSearch.trim()
    if (!q) return availableAccounts
    return availableAccounts.filter((a) => a.includes(q))
  }, [availableAccounts, accountSearch])

  const scopeRows = useMemo(
    () =>
      tree
        ? flattenScope(tree, {
            months: selectedMonths,
            days: selectedDays,
            accounts: selectedAccounts,
          })
        : [],
    [tree, selectedMonths, selectedDays, selectedAccounts]
  )

  const scopeSummary = useMemo(() => {
    const months = new Set(scopeRows.map((r) => r.month))
    const days = new Set(scopeRows.map((r) => `${r.month}-${r.day}`))
    return {
      months: months.size,
      days: days.size,
      pdfs: scopeRows.length,
    }
  }, [scopeRows])

  const totalPages = Math.max(1, Math.ceil(scopeRows.length / PAGE_SIZE))
  const page = Math.min(currentPage, totalPages)
  const pageRows = scopeRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  React.useEffect(() => {
    setCurrentPage(1)
  }, [selectedMonths, selectedDays, selectedAccounts])

  React.useEffect(() => {
    // Remove dias que saíram do recorte de meses
    setSelectedDays((prev) => {
      if (prev.length === 0) return prev
      const valid = new Set(availableDays.map((d) => d.key))
      const next = prev.filter((k) => valid.has(k))
      return next.length === prev.length ? prev : next
    })
  }, [availableDays])

  React.useEffect(() => {
    // Remove contas que saíram do recorte mês/dia
    setSelectedAccounts((prev) => {
      if (prev.length === 0) return prev
      const valid = new Set(availableAccounts)
      const next = prev.filter((a) => valid.has(a))
      return next.length === prev.length ? prev : next
    })
  }, [availableAccounts])

  const allMonthsSelected =
    !!tree &&
    tree.months.length > 0 &&
    selectedMonths.length === tree.months.length

  const allDaysSelected =
    availableDays.length > 0 && selectedDays.length === availableDays.length

  const selectableAccounts = filteredAccounts
  const allVisibleAccountsSelected =
    selectableAccounts.length > 0 &&
    selectableAccounts.every((a) => selectedAccounts.includes(a))

  const onToggleAllMonths = () => {
    if (!tree) return
    if (allMonthsSelected || selectedMonths.length === tree.months.length) {
      setSelectedMonths([])
      setSelectedDays([])
      setSelectedAccounts([])
    } else {
      setSelectedMonths(tree.months.map((m) => m.month))
      setSelectedDays([])
      setSelectedAccounts([])
    }
  }

  const onToggleMonth = (month: string, checked: boolean) => {
    setSelectedMonths((prev) => {
      if (checked) return prev.includes(month) ? prev : [...prev, month]
      return prev.filter((m) => m !== month)
    })
    setSelectedDays([])
    setSelectedAccounts([])
  }

  const onToggleAllDays = () => {
    if (allDaysSelected) {
      setSelectedDays([])
      setSelectedAccounts([])
    } else {
      setSelectedDays(availableDays.map((d) => d.key))
      setSelectedAccounts([])
    }
  }

  const onToggleDay = (key: string, checked: boolean) => {
    setSelectedDays((prev) => {
      if (checked) return prev.includes(key) ? prev : [...prev, key]
      return prev.filter((k) => k !== key)
    })
    setSelectedAccounts([])
  }

  const onToggleAllAccounts = () => {
    if (allVisibleAccountsSelected) {
      setSelectedAccounts((prev) => prev.filter((a) => !selectableAccounts.includes(a)))
    } else {
      setSelectedAccounts((prev) => {
        const next = new Set(prev)
        for (const a of selectableAccounts) next.add(a)
        return Array.from(next)
      })
    }
  }

  const onToggleAccount = (account: string, checked: boolean) => {
    setSelectedAccounts((prev) => {
      if (checked) return prev.includes(account) ? prev : [...prev, account]
      return prev.filter((a) => a !== account)
    })
  }

  const scopeHint = useMemo(() => {
    if (!tree) return null
    if (selectedAccounts.length > 0) {
      return `Escopo: ${selectedAccounts.length} conta(s) selecionada(s)`
    }
    if (selectedDays.length > 0) {
      return `Escopo: ${selectedDays.length} dia(s) completo(s)`
    }
    if (selectedMonths.length > 0) {
      return `Escopo: ${selectedMonths.length} mês(es) completo(s)`
    }
    return `Escopo: ano ${tree.year} completo`
  }, [tree, selectedMonths, selectedDays, selectedAccounts])

  const pendingScopeRows = useMemo(() => {
    if (!tree) return []
    return scopeRows.filter((r) => !processedFileKeys.has(scopeRowKey(r)))
  }, [tree, scopeRows, processedFileKeys, scopeRowKey])

  const alreadyInScopeCount = scopeRows.length - pendingScopeRows.length

  const progressDoneCount = useMemo(
    () => fileProgress.filter((p) => p.status === 'ok' || p.status === 'erro' || p.status === 'ja_processado').length,
    [fileProgress]
  )

  const progressPct =
    fileProgress.length === 0
      ? 0
      : Math.round((progressDoneCount / fileProgress.length) * 100)

  const handleProcess = useCallback(async () => {
    if (isProcessing || !tree || pendingScopeRows.length === 0) return

    setIsProcessing(true)
    setError(null)
    setCurrentFileLabel(null)

    const initial: FinanceiroPelFileProgress[] = pendingScopeRows.map((row, idx) => ({
      id: `${scopeRowKey(row)}::${idx}`,
      year: tree.year,
      month: row.month,
      day: row.day,
      account: row.account,
      fileName: row.fileName,
      status: 'pendente',
    }))
    setFileProgress(initial)

    let okCount = 0
    let errCount = 0
    let skippedCount = 0

    const byDay = new Map<string, typeof pendingScopeRows>()
    for (const row of pendingScopeRows) {
      const key = pelFolderKey(tree.year, row.month, row.day)
      const list = byDay.get(key) ?? []
      list.push(row)
      byDay.set(key, list)
    }

    try {
      for (const [, dayRows] of byDay) {
        const first = dayRows[0]
        setCurrentFileLabel(`Abrindo pasta ${tree.year}/${first.month}/${first.day}...`)

        let folderId: string
        try {
          const pasta = await financeiroExtratosContaService.beginOrResumeFolder({
            year: tree.year,
            month: first.month,
            day: first.day,
            rootFolderName: tree.rootName,
            userId: user?.id ?? null,
          })
          folderId = pasta.id
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Erro ao abrir pasta no banco.'
          setFileProgress((prev) =>
            prev.map((p) =>
              p.year === tree.year &&
              p.month === first.month &&
              p.day === first.day &&
              p.status === 'pendente'
                ? { ...p, status: 'erro', error: message }
                : p
            )
          )
          errCount += dayRows.length
          continue
        }

        let batchOk = 0
        let batchErr = 0

        for (const row of dayRows) {
          const progressId = initial.find(
            (p) =>
              p.month === row.month &&
              p.day === row.day &&
              p.account === row.account &&
              p.fileName === row.fileName
          )?.id

          const mark = (status: FinanceiroPelFileProgress['status'], errorMsg?: string) => {
            setFileProgress((prev) =>
              prev.map((p) =>
                progressId && p.id === progressId
                  ? { ...p, status, error: errorMsg }
                  : p
              )
            )
          }

          const already = processedFileKeys.has(scopeRowKey(row))
          if (already) {
            mark('ja_processado')
            skippedCount += 1
            continue
          }

          mark('enviando')
          setCurrentFileLabel(`${row.month}/${row.day}/${row.fileName}`)

          let fileId: string | null = null
          let webhookAccepted = false
          try {
            // 1) Cria file_id antes do webhook (n8n grava statements com esse id)
            const fileRow = await financeiroExtratosContaService.registerFile({
              folderId,
              year: tree.year,
              month: row.month,
              day: row.day,
              account: row.account,
              fileName: row.fileName,
              status: 'processing',
              userId: user?.id ?? null,
            })
            fileId = fileRow.id

            // 2) Envia PDF + metadados ao webhook
            await sendPelExtratoPdf({
              file: row.file,
              folderId,
              fileId: fileRow.id,
              year: tree.year,
              month: row.month,
              day: row.day,
              fileName: row.fileName,
              account: row.account,
            })
            webhookAccepted = true

            // 3) Confirma ok só depois do webhook aceitar. Falha aqui não rebaixa o arquivo.
            const rowKey = scopeRowKey(row)
            await confirmPelFileOk(fileRow.id, user?.id ?? null)
            forgetUnconfirmedPelFile(rowKey)
            mark('ok')
            okCount += 1
            batchOk += 1
            setProcessedFileKeys((prev) => {
              const next = new Set(prev)
              next.add(rowKey)
              return next
            })
          } catch (err) {
            const message = err instanceof Error ? err.message : 'Erro ao processar arquivo.'
            if (webhookAccepted && fileId) {
              const rowKey = scopeRowKey(row)
              rememberUnconfirmedPelFile({ key: rowKey, fileId })
              mark(
                'erro',
                `Webhook aceitou o PDF, mas a confirmação no banco falhou. O arquivo não será reenviado. ${message}`
              )
              errCount += 1
              setProcessedFileKeys((prev) => {
                const next = new Set(prev)
                next.add(rowKey)
                return next
              })
              console.error(err)
            } else {
              mark('erro', message)
              errCount += 1
              batchErr += 1
              if (fileId) {
                try {
                  await financeiroExtratosContaService.updateFileStatus({
                    fileId,
                    status: 'error',
                    errorMessage: message,
                    userId: user?.id ?? null,
                  })
                } catch (regErr) {
                  console.error(regErr)
                }
              } else {
                try {
                  await financeiroExtratosContaService.registerFile({
                    folderId,
                    year: tree.year,
                    month: row.month,
                    day: row.day,
                    account: row.account,
                    fileName: row.fileName,
                    status: 'error',
                    errorMessage: message,
                    userId: user?.id ?? null,
                  })
                } catch (regErr) {
                  console.error(regErr)
                }
              }
            }
          }
        }

        try {
          await financeiroExtratosContaService.completeFolder({
            folderId,
            arquivosOk: batchOk,
            arquivosErro: batchErr,
          })
        } catch (err) {
          console.error(err)
        }
      }
    } finally {
      setIsProcessing(false)
      setCurrentFileLabel(null)
      await refreshProcessedFiles()
    }

    await logAction(
      'import',
      'other',
      null,
      `Extratos PeL: ${okCount} ok, ${errCount} erro(s), ${skippedCount} já processado(s)`,
      {
        ok: okCount,
        erros: errCount,
        jaProcessados: skippedCount,
        ano: tree.year,
        root: tree.rootName,
      }
    )
  }, [
    isProcessing,
    tree,
    pendingScopeRows,
    processedFileKeys,
    scopeRowKey,
    user?.id,
    refreshProcessedFiles,
    logAction,
  ])

  return (
    <div className="flex-1 p-6 space-y-6">
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700 p-6 space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <input
            ref={(el) => {
              folderInputRef.current = el
              if (el) {
                el.setAttribute('webkitdirectory', '')
                el.setAttribute('directory', '')
              }
            }}
            type="file"
            className="hidden"
            multiple
            onChange={handleFolderChange}
            disabled={isListing || isProcessing}
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => void handleSelectRootClick()}
            disabled={isListing || isProcessing}
          >
            {isListing ? (
              <>
                <ArrowPathIcon className="h-5 w-5 animate-spin" />
                Lendo pastas...
              </>
            ) : (
              <>
                <FolderOpenIcon className="h-5 w-5" />
                Selecionar pasta do ano
              </>
            )}
          </Button>
          <Text size="sm" color="muted">
            Selecione a pasta do ano (ex.: &quot;- 2026&quot;). Em seguida escolha mês, dia ou conta.
          </Text>
        </div>

        {tree && (
          <div className="space-y-4">
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 px-4 py-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Pasta: {tree.rootName}
                  </span>
                  <Text size="xs" color="muted" className="mt-0.5">
                    Ano identificado: {tree.year} · {tree.months.length} mês
                    {tree.months.length !== 1 ? 'es' : ''} · {scopeSummary.pdfs} PDF
                    {scopeSummary.pdfs !== 1 ? 's' : ''} no escopo
                    {(tree.skippedFolderCount > 0 || tree.skippedPdfCount > 0) && (
                      <>
                        {' '}
                        · {tree.skippedFolderCount} pasta
                        {tree.skippedFolderCount !== 1 ? 's' : ''} e {tree.skippedPdfCount} PDF
                        {tree.skippedPdfCount !== 1 ? 's' : ''} ignorado
                        {tree.skippedFolderCount + tree.skippedPdfCount !== 1 ? 's' : ''}
                      </>
                    )}
                  </Text>
                  {scopeHint && (
                    <Text size="xs" color="muted" className="mt-0.5">
                      {scopeHint}
                    </Text>
                  )}
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={cancelSession}
                  disabled={isProcessing}
                >
                  Cancelar
                </Button>
              </div>
            </div>

            {/* Meses */}
            <section className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-800">
              <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-primary-50/60 dark:bg-primary-900/20">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary-600 text-xs font-semibold text-white">
                      1
                    </span>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <CalendarDaysIcon className="h-4 w-4 text-primary-700 dark:text-primary-300" />
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                          Meses
                        </h3>
                      </div>
                      <Text size="xs" color="muted" className="mt-0.5">
                        Nenhum mês marcado = ano inteiro
                      </Text>
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={allMonthsSelected}
                      onChange={onToggleAllMonths}
                    />
                    <span>Selecionar todos ({tree.months.length})</span>
                  </label>
                </div>
              </div>
              <ul
                role="list"
                className="divide-y divide-gray-100 dark:divide-gray-700/80 max-h-48 overflow-y-auto"
              >
                {tree.months.map((m) => (
                  <li
                    key={m.month}
                    className="px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-900/40"
                  >
                    <label className="flex items-center gap-3 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedMonths.includes(m.month)}
                        onChange={(e) => onToggleMonth(m.month, e.target.checked)}
                      />
                      <span className="font-medium flex-1">
                        {m.month} — {m.label}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {m.pdfCount} PDF{m.pdfCount !== 1 ? 's' : ''}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            </section>

            {/* Dias */}
            <section className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-800">
              <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-sky-50/70 dark:bg-sky-900/20">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-sky-600 text-xs font-semibold text-white">
                      2
                    </span>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <CalendarDaysIcon className="h-4 w-4 text-sky-700 dark:text-sky-300" />
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                          Dias
                        </h3>
                      </div>
                      <Text size="xs" color="muted" className="mt-0.5">
                        Nenhum dia marcado = meses do recorte por completo
                      </Text>
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={allDaysSelected}
                      onChange={onToggleAllDays}
                      disabled={availableDays.length === 0}
                    />
                    <span>Selecionar todos ({availableDays.length})</span>
                  </label>
                </div>
              </div>
              <ul
                role="list"
                className="divide-y divide-gray-100 dark:divide-gray-700/80 max-h-48 overflow-y-auto"
              >
                {availableDays.length === 0 ? (
                  <li className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                    Nenhum dia no recorte atual.
                  </li>
                ) : (
                  availableDays.map((d) => (
                    <li
                      key={d.key}
                      className="px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-900/40"
                    >
                      <label className="flex items-center gap-3 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedDays.includes(d.key)}
                          onChange={(e) => onToggleDay(d.key, e.target.checked)}
                        />
                        <span className="font-medium flex-1">
                          {d.monthLabel} / dia {d.day}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {d.pdfCount} PDF{d.pdfCount !== 1 ? 's' : ''}
                        </span>
                      </label>
                    </li>
                  ))
                )}
              </ul>
            </section>

            {/* Contas / arquivos */}
            <section className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-800">
              <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-amber-50/70 dark:bg-amber-900/15">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-amber-600 text-xs font-semibold text-white">
                      3
                    </span>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <DocumentTextIcon className="h-4 w-4 text-amber-700 dark:text-amber-300" />
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                          Contas (arquivos PDF)
                        </h3>
                      </div>
                      <Text size="xs" color="muted" className="mt-0.5">
                        Nenhuma conta marcada = dias do recorte por completo
                      </Text>
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={allVisibleAccountsSelected}
                      onChange={onToggleAllAccounts}
                      disabled={selectableAccounts.length === 0}
                    />
                    <span>
                      Selecionar visíveis ({selectableAccounts.length}
                      {accountSearch.trim() ? ` de ${availableAccounts.length}` : ''})
                    </span>
                  </label>
                </div>
              </div>
              <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700/80 bg-gray-50/80 dark:bg-gray-900/30">
                <div className="max-w-xs">
                  <Input
                    type="search"
                    placeholder="Buscar número de conta..."
                    value={accountSearch}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setAccountSearch(e.target.value)
                    }
                  />
                </div>
              </div>
              <ul
                role="list"
                className="divide-y divide-gray-100 dark:divide-gray-700/80 max-h-56 overflow-y-auto"
              >
                {filteredAccounts.length === 0 ? (
                  <li className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                    Nenhuma conta no recorte atual.
                  </li>
                ) : (
                  filteredAccounts.map((account) => (
                    <li
                      key={account}
                      className="px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-900/40"
                    >
                      <label className="flex items-center gap-3 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedAccounts.includes(account)}
                          onChange={(e) => onToggleAccount(account, e.target.checked)}
                        />
                        <span className="font-medium font-mono">{account}</span>
                      </label>
                    </li>
                  ))
                )}
              </ul>
            </section>
          </div>
        )}
      </div>

      {tree && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-3">
            <div>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Conferência do escopo
              </span>
              <Text size="xs" color="muted" className="mt-0.5">
                {scopeSummary.months} mês{scopeSummary.months !== 1 ? 'es' : ''} ·{' '}
                {scopeSummary.days} dia{scopeSummary.days !== 1 ? 's' : ''} ·{' '}
                {scopeSummary.pdfs} PDF{scopeSummary.pdfs !== 1 ? 's' : ''}
                {alreadyInScopeCount > 0 && (
                  <> · {alreadyInScopeCount} já processado{alreadyInScopeCount !== 1 ? 's' : ''}</>
                )}
              </Text>
            </div>
            <Button
              type="button"
              size="sm"
              onClick={() => void handleProcess()}
              disabled={isProcessing || pendingScopeRows.length === 0}
            >
              {isProcessing ? (
                <>
                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                  Processando...
                </>
              ) : (
                `Processar (${pendingScopeRows.length} PDF${pendingScopeRows.length !== 1 ? 's' : ''})`
              )}
            </Button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Mês
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Dia
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Conta
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Arquivo
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {pageRows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-4 py-6 text-sm text-center text-gray-500 dark:text-gray-400"
                    >
                      Nenhum PDF no escopo selecionado.
                    </td>
                  </tr>
                ) : (
                  pageRows.map((row, idx) => {
                    const key = scopeRowKey(row)
                    const already = processedFileKeys.has(key)
                    return (
                      <tr
                        key={`${row.month}-${row.day}-${row.account}-${row.fileName}-${idx}`}
                        className={`hover:bg-gray-50 dark:hover:bg-gray-900/30 ${
                          already ? 'opacity-60' : ''
                        }`}
                      >
                        <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                          {row.month} — {row.monthLabel}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">
                          {row.day}
                        </td>
                        <td className="px-4 py-2 text-sm font-mono text-gray-700 dark:text-gray-300">
                          {row.account}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">
                          {row.fileName}
                        </td>
                        <td className="px-4 py-2 text-xs">
                          {already ? (
                            <span className="text-amber-700 dark:text-amber-400">já processado</span>
                          ) : (
                            <span className="text-gray-500 dark:text-gray-400">pendente</span>
                          )}
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>

          {scopeRows.length > PAGE_SIZE && (
            <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-2">
              <Text size="xs" color="muted">
                Página {page} de {totalPages} ({scopeRows.length} itens)
              </Text>
              <div className="flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={page <= 1 || isProcessing}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                >
                  Anterior
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={page >= totalPages || isProcessing}
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                >
                  Próxima
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {fileProgress.length > 0 && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {isProcessing ? 'Processamento em andamento' : 'Processamento concluído'}
              </span>
              <Text size="xs" color="muted">
                {progressDoneCount} de {fileProgress.length} · {progressPct}%
              </Text>
            </div>
            {currentFileLabel && (
              <Text size="xs" color="muted">
                Atual: {currentFileLabel}
              </Text>
            )}
            <div className="h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
              <div
                className="h-full bg-primary-600 transition-all duration-300"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>
          <ul
            role="list"
            className="divide-y divide-gray-200 dark:divide-gray-700 max-h-72 overflow-y-auto"
          >
            {fileProgress.map((p) => (
              <li key={p.id} className="px-4 py-2.5 flex items-start gap-3 text-sm">
                {p.status === 'ok' && (
                  <CheckCircleIcon className="h-5 w-5 text-green-600 dark:text-green-400 shrink-0" />
                )}
                {(p.status === 'erro' || p.status === 'ja_processado') && (
                  <ExclamationTriangleIcon
                    className={`h-5 w-5 shrink-0 ${
                      p.status === 'ja_processado'
                        ? 'text-amber-500'
                        : 'text-red-600 dark:text-red-400'
                    }`}
                  />
                )}
                {p.status === 'enviando' && (
                  <ArrowPathIcon className="h-5 w-5 text-primary-600 animate-spin shrink-0" />
                )}
                {p.status === 'pendente' && (
                  <span className="h-5 w-5 rounded-full border border-gray-300 dark:border-gray-600 shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-gray-800 dark:text-gray-200 truncate">
                    {p.month}/{p.day}/{p.fileName}
                  </div>
                  <Text size="xs" color="muted">
                    Conta {p.account} ·{' '}
                    {p.status === 'pendente' && 'pendente'}
                    {p.status === 'enviando' && 'enviando ao webhook...'}
                    {p.status === 'ok' && 'ok'}
                    {p.status === 'erro' && (p.error || 'erro')}
                    {p.status === 'ja_processado' && 'já processado'}
                  </Text>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

export default ExtratosContaTab
